/**
 * Owner: Masa Bayu
 * File: geometry.ts
 * Date:Time: 2026-09-28 07:29
 * Description: Bounding box · text style for Document IR layout (Eyes).
 */

import { isRecord } from './guards';

export interface QXDocumentBBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface QXDocumentTextStyle {
  readonly fontName: string | null;
  readonly fontSize: number | null;
  readonly bold: boolean;
  readonly italic: boolean;
  /** CAPTURED when Eyes/provider set it (e.g. DOCX). Never invent from PDF fontName. */
  readonly underline: boolean;
}

function assertFinite(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label} must be a finite number`);
  }
  return value;
}

export function createQXDocumentBBox(options: {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}): QXDocumentBBox {
  if (!isRecord(options)) {
    throw new Error('bbox must be an object');
  }
  return Object.freeze({
    x: assertFinite(options.x, 'bbox.x'),
    y: assertFinite(options.y, 'bbox.y'),
    width: assertFinite(options.width, 'bbox.width'),
    height: assertFinite(options.height, 'bbox.height'),
  });
}

export function createQXDocumentTextStyle(options?: {
  readonly fontName?: string | null;
  readonly fontSize?: number | null;
  readonly bold?: boolean;
  readonly italic?: boolean;
  readonly underline?: boolean;
}): QXDocumentTextStyle {
  const fontSize =
    options?.fontSize === undefined || options.fontSize === null
      ? null
      : assertFinite(options.fontSize, 'style.fontSize');
  const fontName =
    options?.fontName === undefined || options.fontName === null
      ? null
      : String(options.fontName).trim() || null;
  return Object.freeze({
    fontName,
    fontSize,
    bold: options?.bold === true,
    italic: options?.italic === true,
    underline: options?.underline === true,
  });
}
